import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Typography,
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Container,
  useTheme,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { tokens } from '../../theme/tokens';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';
import { apiConfig } from '../../utils/apiConfig';
import { resolveServerQuizId } from '../../config/quizServerMap';
import { buildRegexVariants, findMatchingKey } from '../../utils/quizMatching';
import { getOverrideQuestions } from '../../services/quizEditorStorage';

export interface QuizPageConfig {
  storageKey: string;
  sheetCandidates: string[];
  title: string;
  homeRoute: string;
  cardTitle: string;
}

export type QuizQuestion = {
  question: string;
  options: string[];
  correctIndexes: number[];
};

type ProgressState = {
  index: number;
  score: number;
  questionOutcomes: Record<number, 'correct' | 'wrong'>;
};

type StatsState = {
  completed: number;
  totalCorrect: number;
  bestScore: number;
};

type UiState = {
  mode: 'answering' | 'feedback';
  selectedOptions: number[];
  feedback: string | null;
  wasCorrect: boolean | null;
  answeredIndex: number | null;
};

type ResultSummary = {
  score: number;
  totalQuestions: number;
  percentage: number;
  newHighscore: boolean;
  successRate: number;
  totalCompleted: number;
  bestScore: number;
  earnedJokerCount: number;
};

const QUIZ_PATH = '/assets/Quiz.xlsx';
const GLOBAL_JOKER_COUNT_STORAGE_KEY = 'quiz_global_joker_count';
const LEGACY_GLOBAL_JOKER_STORAGE_KEY = 'quiz_global_joker_available';

const DEFAULT_PROGRESS: ProgressState = { index: 0, score: 0, questionOutcomes: {} };
const DEFAULT_STATS: StatsState = { completed: 0, totalCorrect: 0, bestScore: 0 };
const DEFAULT_UI_STATE: UiState = {
  mode: 'answering',
  selectedOptions: [],
  feedback: null,
  wasCorrect: null,
  answeredIndex: null
};

const toTrimmedString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value) ? value.toString() : value.toString();
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return value.toString().trim();
};

const equalsIgnoreCase = (a: string, b: string): boolean =>
  a.localeCompare(b, undefined, { sensitivity: 'accent', usage: 'search' }) === 0;

const rowLooksLikeHeader = (row: any[]): boolean => {
  if (!Array.isArray(row)) return false;
  return row.some((cell) => {
    const value = toTrimmedString(cell).toLowerCase();
    if (!value) return false;
    return (
      value.includes('frage') ||
      value.includes('question') ||
      value.includes('antwort') ||
      value.includes('option') ||
      value === 'a' ||
      value === 'b'
    );
  });
};

const parseAndroidStyleRows = (rows: any[][]): QuizQuestion[] => {
  const questions: QuizQuestion[] = [];
  if (!rows || rows.length === 0) return questions;

  const firstRowIsHeader = rowLooksLikeHeader(rows[0]);
  const startIndex = firstRowIsHeader ? 1 : 0;

  for (let i = startIndex; i < rows.length; i += 1) {
    const row = rows[i];
    if (!Array.isArray(row)) continue;

    const question = toTrimmedString(row[1]);
    if (!question) continue;

    const options: string[] = [];
    for (let col = 2; col <= 5; col += 1) {
      const optionText = toTrimmedString(row[col]);
      if (optionText) options.push(optionText);
    }

    if (options.length < 2) continue;

    const correctText = toTrimmedString(row[6]);
    if (!correctText) continue;

    let correctIndexes = deriveCorrectIndexes(correctText, options);
    if (correctIndexes.length === 0) {
      const fallbackIndex = deriveCorrectIndex(correctText, options);
      if (fallbackIndex >= 0) correctIndexes = [fallbackIndex];
    }

    questions.push({
      question,
      options,
      correctIndexes: normalizeCorrectIndexes(correctIndexes, options.length)
    });
  }

  return questions;
};

export const parseSheetRowsToQuestions = (rows: any[][]): QuizQuestion[] => {
  const structuredQuestions = parseAndroidStyleRows(rows);
  if (structuredQuestions.length > 0) {
    return structuredQuestions;
  }
  return parseSheetRowsToQuestionsFallback(rows);
};

const parseSheetRowsToQuestionsFallback = (rows: any[][]): QuizQuestion[] => {
  const res: QuizQuestion[] = [];
  if (!rows || rows.length === 0) return res;

  const header = Array.isArray(rows[0])
    ? rows[0].map((cell) => toTrimmedString(cell).toLowerCase())
    : [];
  const hasHeader = header.some(
    (h) =>
      h.includes('question') ||
      h.includes('frage') ||
      h === 'q' ||
      h.includes('antwort') ||
      h.includes('correct') ||
      h.includes('richtig')
  );

  const start = hasHeader ? 1 : 0;
  for (let i = start; i < rows.length; i += 1) {
    const row = rows[i];
    if (!Array.isArray(row) || row.length === 0) continue;

    if (hasHeader) {
      const qIdx = header.findIndex(
        (h) => h.includes('question') || h.includes('frage') || h === 'q'
      );
      if (qIdx === -1) continue;

      const correctIdx = header.findIndex(
        (h) => h.includes('correct') || h.includes('antwort') || h.includes('richtig')
      );

      const optionsIdxs = header
        .map((h, idx) => ({ h, idx }))
        .filter(({ h, idx }) => {
          if (idx === qIdx || idx === correctIdx) return false;
          return /^(a|b|c|d|option|antwort|choice)/.test(h) || h.length === 1;
        })
        .map(({ idx }) => idx);

      const question = toTrimmedString(row[qIdx]);
      if (!question) continue;

      const options = optionsIdxs
        .map((idx) => toTrimmedString(row[idx]))
        .filter((opt) => opt.length > 0);

      if (options.length < 2) continue;

      const correctText =
        correctIdx >= 0 ? toTrimmedString(row[correctIdx]) : options[options.length - 1];

      let correctIndexes = deriveCorrectIndexes(correctText, options);
      if (correctIndexes.length === 0) {
        const fallbackIndex = deriveCorrectIndex(correctText, options);
        if (fallbackIndex >= 0) correctIndexes = [fallbackIndex];
      }

      res.push({
        question,
        options,
        correctIndexes: normalizeCorrectIndexes(correctIndexes, options.length)
      });
    } else {
      const question = toTrimmedString(row[0]);
      if (!question) continue;

      const cellsAfterQuestion = row
        .slice(1)
        .map((cell: any) => toTrimmedString(cell))
        .filter((value) => value.length > 0);
      if (cellsAfterQuestion.length === 0) continue;

      const options = cellsAfterQuestion.slice(0, Math.max(0, cellsAfterQuestion.length - 1));
      let correctText = cellsAfterQuestion[cellsAfterQuestion.length - 1] || '';

      if (options.length < 2) continue;
      if (!correctText) {
        correctText = options[options.length - 1];
      }

      let correctIndexes = deriveCorrectIndexes(correctText, options);
      if (correctIndexes.length === 0) {
        const fallbackIndex = deriveCorrectIndex(correctText, options);
        if (fallbackIndex >= 0) correctIndexes = [fallbackIndex];
      }

      res.push({
        question,
        options,
        correctIndexes: normalizeCorrectIndexes(correctIndexes, options.length)
      });
    }
  }

  return res;
};

const normalizeCorrectIndexes = (indexes: number[], length: number): number[] => {
  if (!length) return [];
  const unique = Array.from(new Set(indexes.filter((idx) => Number.isFinite(idx))));
  const inRange = unique.filter((idx) => idx >= 0 && idx < length);
  return inRange.length > 0 ? inRange : [0];
};

const deriveCorrectIndexes = (value: unknown, options: string[]): number[] => {
  if (!options.length) return [];
  const candidate = toTrimmedString(value);
  if (!candidate) return [];

  // Tokens durch Trennzeichen splitten
  const tokens = candidate
    .split(/[;,/|]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);

  const collected: number[] = [];
  const pushIndex = (idx: number) => {
    if (idx >= 0 && idx < options.length && !collected.includes(idx)) {
      collected.push(idx);
    }
  };

  const tryToken = (token: string) => {
    // Exakter Match mit einer Option
    const directMatch = options.findIndex((opt) => equalsIgnoreCase(opt, token));
    if (directMatch >= 0) {
      pushIndex(directMatch);
      return;
    }
    // Einzelner Buchstabe oder Zahl
    const derived = deriveCorrectIndex(token, options);
    if (derived >= 0) pushIndex(derived);
  };

  if (tokens.length > 0) {
    tokens.forEach(tryToken);
  } else {
    tryToken(candidate);
  }

  // Nur bei kurzen Texten (max 10 Zeichen) versuchen Buchstaben/Zahlen zu extrahieren
  // um zu vermeiden, dass aus Texten wie "Brandabschnitt" die Buchstaben B,A,D,C extrahiert werden
  if (collected.length === 0 && candidate.length <= 10) {
    // Prüfen ob der Text hauptsächlich aus Buchstaben-Indizes besteht (A, B, C, D oder 1, 2, 3, 4)
    const cleanedCandidate = candidate.replace(/[^A-Za-z0-9]/g, '');
    const looksLikeIndexList = /^[A-Da-d1-4]+$/.test(cleanedCandidate);
    
    if (looksLikeIndexList) {
      const letters = candidate.match(/[A-Da-d]/g) || [];
      letters.forEach((letter) => {
        const upper = letter.toUpperCase();
        const idx = upper.charCodeAt(0) - 65;
        pushIndex(idx);
      });
    }
  }

  // Nummerische Fallback nur bei kurzen Texten
  if (collected.length === 0 && candidate.length <= 10) {
    const numbers = candidate.match(/\d+/g) || [];
    numbers.forEach((num) => {
      const parsed = Number(num);
      if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= options.length) {
        pushIndex(parsed - 1);
      }
    });
  }

  return collected;
};

const deriveCorrectIndex = (value: unknown, options: string[]): number => {
  if (!options.length) return 0;
  const candidate = toTrimmedString(value);
  if (!candidate) return -1;

  const normalizedCandidate = candidate.toLowerCase();
  const normalizedOptions = options.map((opt) => opt.toLowerCase());

  // Exakter Match
  let matchIndex = normalizedOptions.findIndex((opt) => opt === normalizedCandidate);
  if (matchIndex >= 0) return matchIndex;

  // Case-insensitive Match
  matchIndex = options.findIndex((opt) => equalsIgnoreCase(opt, candidate));
  if (matchIndex >= 0) return matchIndex;

  // Einzelner Buchstabe A-D
  const trimmedCandidate = candidate.trim();
  if (trimmedCandidate.length === 1) {
    const letter = trimmedCandidate.toUpperCase();
    if (letter >= 'A' && letter <= 'D') {
      return letter.charCodeAt(0) - 65;
    }
  }

  // Nur bei kurzen Texten nach Buchstaben-Pattern suchen
  if (candidate.length <= 5) {
    const letterMatch = candidate.match(/^([A-Da-d])$/);
    if (letterMatch) {
      const letter = letterMatch[1].toUpperCase();
      return letter.charCodeAt(0) - 65;
    }
  }

  // Numerisch (1-4 für Antwortindex)
  const numeric = Number(candidate);
  if (!Number.isNaN(numeric) && Number.isFinite(numeric) && Number.isInteger(numeric)) {
    if (numeric >= 1 && numeric <= options.length) {
      return numeric - 1;
    }
  }

  // Nummerisches Pattern bei kurzen Texten
  if (candidate.length <= 5) {
    const numericMatch = candidate.match(/^(\d+)$/);
    if (numericMatch) {
      const parsed = Number(numericMatch[1]);
      if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= options.length) {
        return parsed - 1;
      }
    }
  }

  return -1;
};

export const createQuizPage = ({
  storageKey,
  sheetCandidates,
  title,
  homeRoute,
  cardTitle
}: QuizPageConfig): React.FC => {
  const Component: React.FC = () => {
    const theme = useTheme();
    const { token, refreshProfile, canManageQuiz, canAnswerQuizzes } = useAuth();
    const darkMode = theme.palette.mode === 'dark';
    // Token-based color helpers
    const accentColor = tokens.colors.primary;
    const textPrimary = darkMode ? tokens.colors.dark.text : tokens.colors.light.text;
    const successColor = '#2e7d32'; // Success state
    const errorColor = '#c62828'; // Error state
    const successBgLight = 'rgba(46, 125, 50, 0.12)';
    const successBgDark = 'rgba(46, 125, 50, 0.25)';
    const errorBgLight = 'rgba(198, 40, 40, 0.12)';
    const errorBgDark = 'rgba(198, 40, 40, 0.25)';
    const hoverBgLight = 'rgba(0,0,0,0.02)';
    const hoverBgDark = 'rgba(255,255,255,0.05)'
    const progressKeyRef = useRef(`quiz_${storageKey.toLowerCase()}_progress`);
    const statsKeyRef = useRef(`quiz_${storageKey.toLowerCase()}_stats`);
    const uiStateKeyRef = useRef(`quiz_${storageKey.toLowerCase()}_ui_state`);
    const outcomeLockKeyRef = useRef(`quiz_${storageKey.toLowerCase()}_outcome_lock`);
    const sheetRegexesRef = useRef(sheetCandidates.flatMap(buildRegexVariants));
    const serverQuizId = resolveServerQuizId(storageKey, sheetCandidates);

    const STORAGE_PROGRESS_KEY = progressKeyRef.current;
    const STORAGE_STATS_KEY = statsKeyRef.current;
    const STORAGE_UI_STATE_KEY = uiStateKeyRef.current;
    const STORAGE_OUTCOME_LOCK_KEY = outcomeLockKeyRef.current;
    const sheetRegexes = sheetRegexesRef.current;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [questions, setQuestions] = useState<QuizQuestion[]>([]);
    const [progress, setProgress] = useState<ProgressState>(() => {
      const base = loadProgress(STORAGE_PROGRESS_KEY);
      const lockedOutcomes = loadOutcomeLocks(STORAGE_OUTCOME_LOCK_KEY);
      if (Object.keys(lockedOutcomes).length === 0) return base;
      const mergedOutcomes: Record<number, 'correct' | 'wrong'> = {
        ...(base.questionOutcomes || {}),
        ...lockedOutcomes
      };
      return {
        ...base,
        questionOutcomes: mergedOutcomes,
        score: Object.values(mergedOutcomes).filter((state) => state === 'correct').length
      };
    });
    const [stats, setStats] = useState<StatsState>(() => loadStats(STORAGE_STATS_KEY));
    const [uiState, setUiState] = useState<UiState>(() => loadUiState(STORAGE_UI_STATE_KEY));
    const { selectedOptions, mode, feedback, wasCorrect, answeredIndex } = uiState;
    const [showResultDialog, setShowResultDialog] = useState(false);
    const [resultSummary, setResultSummary] = useState<ResultSummary | null>(null);
    const [jokerCount, setJokerCount] = useState<number>(() => loadGlobalJokerCount());
    const [showJokerInfoDialog, setShowJokerInfoDialog] = useState(false);
    const answeredInCurrentSessionRef = useRef<Set<number>>(new Set());

    const fetchJokerBalance = useCallback(async (): Promise<number | null> => {
      if (!token) return null;
      try {
        const res = await fetch(`${apiConfig.apiUrl}?action=getJokerBalance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.success) return null;
        const count = Number(data.jokerCount);
        return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : null;
      } catch {
        return null;
      }
    }, [token]);

    const grantJoker = useCallback(async (amount = 1): Promise<number | null> => {
      if (!token || amount <= 0) return null;
      try {
        const res = await fetch(apiConfig.apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ action: 'grantQuizJoker', amount })
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.success) return null;
        const count = Number(data.jokerCount);
        return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : null;
      } catch {
        return null;
      }
    }, [token]);

    const consumeJoker = useCallback(async (): Promise<number | null> => {
      if (!token) return null;
      try {
        const res = await fetch(apiConfig.apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ action: 'consumeJoker' })
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.success) return null;
        const count = Number(data.jokerCount);
        return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : null;
      } catch {
        return null;
      }
    }, [token]);

    const fetchServerHighscore = useCallback(async (): Promise<number | null> => {
      if (!token || !serverQuizId) return null;
      try {
        const url = `${apiConfig.apiUrl}?action=getQuizScore&quiz_id=${encodeURIComponent(serverQuizId)}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data || typeof data[serverQuizId] === 'undefined') return null;
        const score = Number(data[serverQuizId]);
        return Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : 0;
      } catch {
        return null;
      }
    }, [serverQuizId, token]);

    const postHighscoreIfBetter = useCallback(async (score: number): Promise<boolean> => {
      if (!token || !serverQuizId) return false;
      if (!Number.isFinite(score) || score <= 0) return false;
      try {
        const body: Record<string, number> = { [serverQuizId]: Math.round(score) };
        const res = await fetch(apiConfig.apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        });
        // Server aktualisiert nur, wenn besser – 200/OK auch bei keiner Änderung möglich
        if (res.ok) {
          try { await refreshProfile(); } catch {}
          return true;
        }
        return false;
      } catch {
        return false;
      }
    }, [refreshProfile, serverQuizId, token]);

    useEffect(() => {
      let cancelled = false;
      const load = async () => {
        try {
          const override = getOverrideQuestions([storageKey, ...sheetCandidates]);
          if (override) {
            const normalized = override.questions.map((q) => ({
              ...q,
              correctIndexes: normalizeCorrectIndexes(q.correctIndexes || [], q.options.length)
            }));
            if (!cancelled) {
              setQuestions(normalized);
              setLoading(false);
            }
            return;
          }
          const resp = await fetch(QUIZ_PATH);
          if (!resp.ok) throw new Error('Quiz-Datei nicht gefunden');
          const buffer = await resp.arrayBuffer();
          const XLSX = await import('xlsx');
          const workbook = XLSX.read(buffer, { type: 'array' });
          const sheetName = findMatchingKey(workbook.SheetNames, sheetCandidates) ||
            workbook.SheetNames.find((name) => sheetRegexes.some((regex) => regex.test(name)));
          if (!sheetName) {
            throw new Error('Kein passendes Tabellenblatt gefunden. Bitte Namen in Quiz.xlsx prüfen.');
          }
          const sheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
          const parsedQuestions = parseSheetRowsToQuestions(rows);

          if (!cancelled) {
            setQuestions(parsedQuestions);
            setLoading(false);
          }
        } catch (err: any) {
          console.error(err);
          if (!cancelled) {
            setError(err?.message || 'Fehler beim Laden des Quiz.');
            setLoading(false);
          }
        }
      };

      load();
      return () => {
        cancelled = true;
      };
    }, [sheetRegexes]);

    // Beim Start: Server-Highscore abrufen und lokale Stats ggf. anpassen; ggf. lokalen Bestwert hochladen
    useEffect(() => {
      let active = true;
      const sync = async () => {
        if (!token || !serverQuizId) return;
        // Warte, bis Fragen geladen wurden, damit Stats angezeigt werden können
        if (loading) return;
        const serverScore = await fetchServerHighscore();
        if (!active) return;
        if (serverScore != null) {
          setStats((prev) => {
            const nextBest = Math.max(prev.bestScore, serverScore);
            return nextBest !== prev.bestScore ? { ...prev, bestScore: nextBest } : prev;
          });
          const localBest = stats.bestScore;
          if (localBest > (serverScore ?? 0)) {
            await postHighscoreIfBetter(localBest);
          }
        }
      };
      sync();
      return () => { active = false; };
    }, [fetchServerHighscore, loading, postHighscoreIfBetter, serverQuizId, stats.bestScore, token]);

    useEffect(() => {
      let active = true;
      const syncJoker = async () => {
        const serverCount = await fetchJokerBalance();
        if (!active || serverCount == null) return;
        setJokerCount(serverCount);
        saveGlobalJokerCount(serverCount);
      };
      syncJoker();
      return () => { active = false; };
    }, [fetchJokerBalance]);

    useEffect(() => {
      if (loading) return;

      setProgress((prev) => {
        if (questions.length === 0) {
          return prev;
        }

        const normalizedOutcomes = sanitizeOutcomeMap(prev.questionOutcomes, questions.length);
        const scoreFromOutcomes = Object.values(normalizedOutcomes).filter((state) => state === 'correct').length;
        const safeIndex = Math.min(Math.max(prev.index, 0), questions.length - 1);
        const safeScore = Math.min(Math.max(scoreFromOutcomes, 0), questions.length);
        if (
          safeIndex !== prev.index ||
          safeScore !== prev.score ||
          JSON.stringify(normalizedOutcomes) !== JSON.stringify(prev.questionOutcomes || {})
        ) {
          return { index: safeIndex, score: safeScore, questionOutcomes: normalizedOutcomes };
        }

        return prev;
      });
    }, [loading, questions.length]);

    useEffect(() => {
      if (loading || questions.length === 0) return;

      const safeCurrentIndex = Math.min(Math.max(progress.index, 0), questions.length - 1);
      const currentOutcome = progress.questionOutcomes?.[safeCurrentIndex];
      const uiStateBelongsToCurrentQuestion = answeredIndex === safeCurrentIndex;
      const modeShouldReset = mode === 'feedback' && !uiStateBelongsToCurrentQuestion;
      const selectedAreValid = selectedOptions.every((idx) => idx >= 0 && idx < questions[safeCurrentIndex].options.length);

      if (currentOutcome) {
        const answeredInCurrentSession = answeredInCurrentSessionRef.current.has(safeCurrentIndex);
        if (!answeredInCurrentSession) {
          if (safeCurrentIndex + 1 < questions.length) {
            setProgress((prev) => ({
              ...prev,
              index: safeCurrentIndex + 1
            }));
            setUiState(DEFAULT_UI_STATE);
            return;
          }

          setUiState({
            mode: 'feedback',
            selectedOptions: [],
            wasCorrect: currentOutcome === 'correct',
            feedback:
              currentOutcome === 'correct'
                ? 'Diese Frage wurde bereits gewertet. Bitte Ergebnis oeffnen.'
                : 'Diese Frage wurde bereits als falsch gewertet. Nach Aktualisieren ist kein Joker mehr fuer diese Frage moeglich. Bitte Ergebnis oeffnen.',
            answeredIndex: safeCurrentIndex
          });
          return;
        }
      }

      if (currentOutcome && mode === 'answering') {
        const question = questions[safeCurrentIndex];
        const correctIndexes = normalizeCorrectIndexes(question.correctIndexes || [], question.options.length);
        const correctText = correctIndexes
          .map((idx) => question.options[idx])
          .filter(Boolean)
          .join(', ');

        setUiState({
          mode: 'feedback',
          selectedOptions: [],
          wasCorrect: currentOutcome === 'correct',
          feedback:
            currentOutcome === 'correct'
              ? 'Richtig! (bereits gewertet)'
              : `Diese Frage wurde bereits als falsch gewertet. Richtige Antwort: ${correctText}`,
          answeredIndex: safeCurrentIndex
        });
        return;
      }

      if (modeShouldReset || !selectedAreValid) {
        setUiState(DEFAULT_UI_STATE);
      }
    }, [answeredIndex, loading, mode, progress.index, progress.questionOutcomes, questions, selectedOptions]);

    useEffect(() => {
      if (typeof window === 'undefined') return;
      if (loading || questions.length === 0) return;
      try {
        window.localStorage.setItem(STORAGE_PROGRESS_KEY, JSON.stringify(progress));
        saveOutcomeLocks(STORAGE_OUTCOME_LOCK_KEY, progress.questionOutcomes || {});
      } catch (err) {
        console.warn('Fortschritt konnte nicht gespeichert werden:', err);
      }
  }, [progress, loading, questions.length, STORAGE_PROGRESS_KEY, STORAGE_OUTCOME_LOCK_KEY]);

    useEffect(() => {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(STORAGE_STATS_KEY, JSON.stringify(stats));
      } catch (err) {
        console.warn('Statistiken konnten nicht gespeichert werden:', err);
      }
  }, [stats, STORAGE_STATS_KEY]);

    useEffect(() => {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(STORAGE_UI_STATE_KEY, JSON.stringify(uiState));
      } catch (err) {
        console.warn('Quiz-UI-Zustand konnte nicht gespeichert werden:', err);
      }
    }, [uiState, STORAGE_UI_STATE_KEY]);

    const totalQuestions = questions.length;
    const currentIndex = totalQuestions > 0 ? Math.min(progress.index, totalQuestions - 1) : 0;
    const currentQuestion = totalQuestions > 0 ? questions[currentIndex] : null;

    const highScoreLabel = `Highscore: ${stats.bestScore}%`;
    const successRate =
      stats.completed > 0 && totalQuestions > 0
        ? (stats.totalCorrect / (totalQuestions * stats.completed)) * 100
        : 0;
    const successRateLabel = `Erfolgsquote: ${successRate.toFixed(2)}%`;
    const questionCounterLabel = totalQuestions
      ? `Frage ${currentIndex + 1} von ${totalQuestions}`
      : 'Keine Fragen vorhanden';

    const currentQuestionOutcome = progress.questionOutcomes?.[currentIndex];
    const submitDisabled =
      !currentQuestion ||
      (mode === 'answering' && (selectedOptions.length === 0 || Boolean(currentQuestionOutcome)));
    const feedbackColor = wasCorrect == null ? textPrimary : wasCorrect ? successColor : errorColor;
    const canUseJoker =
      mode === 'feedback' &&
      wasCorrect === false &&
      answeredIndex === currentIndex &&
      answeredInCurrentSessionRef.current.has(currentIndex) &&
      jokerCount > 0;

    const persistProgressImmediately = (next: ProgressState) => {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(STORAGE_PROGRESS_KEY, JSON.stringify(next));
        saveOutcomeLocks(STORAGE_OUTCOME_LOCK_KEY, next.questionOutcomes || {});
      } catch {
        // ignore immediate persist errors
      }
    };

    const persistUiStateImmediately = (next: UiState) => {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(STORAGE_UI_STATE_KEY, JSON.stringify(next));
      } catch {
        // ignore immediate persist errors
      }
    };

    const toggleOption = (index: number) => {
      if (mode === 'feedback' || currentQuestionOutcome) return;
      setUiState((prev) => ({
        ...prev,
        selectedOptions: prev.selectedOptions.includes(index)
          ? prev.selectedOptions.filter((item) => item !== index)
          : [...prev.selectedOptions, index]
      }));
    };

    const handleEvaluateAnswer = () => {
      if (!currentQuestion || selectedOptions.length === 0) return;
      if (progress.questionOutcomes?.[currentIndex]) {
        return;
      }
      const correctIndexes = normalizeCorrectIndexes(currentQuestion.correctIndexes || [], currentQuestion.options.length);
      const selectedSorted = [...selectedOptions].sort((a, b) => a - b);
      const correctSorted = [...correctIndexes].sort((a, b) => a - b);
      const isCorrect =
        selectedSorted.length === correctSorted.length &&
        selectedSorted.every((value, idx) => value === correctSorted[idx]);
      const nextFeedback =
        isCorrect
          ? 'Richtig!'
          : `Leider falsch. Richtige Antwort: ${correctSorted
              .map((idx) => currentQuestion.options[idx])
              .filter(Boolean)
              .join(', ')}`;

      const nextUiState: UiState = {
        ...uiState,
        mode: 'feedback',
        wasCorrect: isCorrect,
        feedback: nextFeedback,
        answeredIndex: currentIndex
      };

      const nextOutcomes: Record<number, 'correct' | 'wrong'> = {
        ...(progress.questionOutcomes || {}),
        [currentIndex]: isCorrect ? 'correct' : 'wrong'
      };
      const nextScore = Object.values(nextOutcomes).filter((state) => state === 'correct').length;
      const nextProgress: ProgressState = {
        ...progress,
        questionOutcomes: nextOutcomes,
        score: nextScore
      };

      persistUiStateImmediately(nextUiState);
      persistProgressImmediately(nextProgress);
      answeredInCurrentSessionRef.current.add(currentIndex);
      setUiState(nextUiState);
      setProgress(nextProgress);
    };

    const finalizeQuiz = async () => {
      if (!currentQuestion) return;
      const total = totalQuestions || 1;
      const finalScore = progress.score;
      const percentage = Math.round((finalScore / total) * 100);
      const earnedJokerCount = 1;
      const nextStats: StatsState = {
        completed: stats.completed + 1,
        totalCorrect: stats.totalCorrect + finalScore,
        bestScore: Math.max(stats.bestScore, percentage)
      };
      const successAfterRun =
        nextStats.completed > 0 && total > 0
          ? (nextStats.totalCorrect / (total * nextStats.completed)) * 100
          : 0;

      setStats(nextStats);
      setResultSummary({
        score: finalScore,
        totalQuestions: total,
        percentage,
        newHighscore: percentage > stats.bestScore,
        successRate: successAfterRun,
        totalCompleted: nextStats.completed,
        bestScore: nextStats.bestScore,
        earnedJokerCount
      });
      setShowResultDialog(true);

      const localNextCount = Math.max(0, jokerCount) + earnedJokerCount;
      setJokerCount(localNextCount);
      saveGlobalJokerCount(localNextCount);
      const serverCount = await grantJoker(earnedJokerCount);
      if (serverCount != null) {
        setJokerCount(serverCount);
        saveGlobalJokerCount(serverCount);
      }

      // Nach Abschluss Versuch: neuen Highscore (falls verbessert) direkt hochladen
      if (percentage > stats.bestScore) {
        // Fire-and-forget; Fehler werden still geschluckt
        postHighscoreIfBetter(percentage);
      }

      setProgress(DEFAULT_PROGRESS);
      clearOutcomeLocks(STORAGE_OUTCOME_LOCK_KEY);
      answeredInCurrentSessionRef.current.clear();
      setUiState(DEFAULT_UI_STATE);
    };

    const handleUseJoker = async () => {
      if (!canUseJoker) return;
      const localCount = Math.max(0, jokerCount - 1);
      setJokerCount(localCount);
      saveGlobalJokerCount(localCount);

      const serverCount = await consumeJoker();
      if (serverCount != null) {
        setJokerCount(serverCount);
        saveGlobalJokerCount(serverCount);
      }

      setProgress((prev) => {
        const nextOutcomes = { ...(prev.questionOutcomes || {}) };
        delete nextOutcomes[currentIndex];
        const nextScore = Object.values(nextOutcomes).filter((state) => state === 'correct').length;
        const nextProgress = { ...prev, questionOutcomes: nextOutcomes, score: nextScore };
        persistProgressImmediately(nextProgress);
        return nextProgress;
      });
      answeredInCurrentSessionRef.current.delete(currentIndex);
      persistUiStateImmediately(DEFAULT_UI_STATE);
      setUiState(DEFAULT_UI_STATE);
    };

    const handlePrimaryAction = () => {
      if (!currentQuestion) return;
      if (mode === 'answering') {
        handleEvaluateAnswer();
        return;
      }

      if (currentIndex + 1 >= totalQuestions) {
        finalizeQuiz();
      } else {
        const nextProgress = { ...progress, index: progress.index + 1, score: progress.score };
        persistProgressImmediately(nextProgress);
        persistUiStateImmediately(DEFAULT_UI_STATE);
        setProgress(nextProgress);
        setUiState(DEFAULT_UI_STATE);
      }
    };

    const handleReset = () => {
      const confirmReset = typeof window === 'undefined' ? true : window.confirm('Quiz zurücksetzen?');
      if (!confirmReset) return;

      persistProgressImmediately(DEFAULT_PROGRESS);
      clearOutcomeLocks(STORAGE_OUTCOME_LOCK_KEY);
      persistUiStateImmediately(DEFAULT_UI_STATE);
      answeredInCurrentSessionRef.current.clear();
      setProgress(DEFAULT_PROGRESS);
      setUiState(DEFAULT_UI_STATE);
    };

    const handleDialogClose = () => {
      setShowResultDialog(false);
      setResultSummary(null);
      persistProgressImmediately(DEFAULT_PROGRESS);
      clearOutcomeLocks(STORAGE_OUTCOME_LOCK_KEY);
      persistUiStateImmediately(DEFAULT_UI_STATE);
      answeredInCurrentSessionRef.current.clear();
      setProgress(DEFAULT_PROGRESS);
      setUiState(DEFAULT_UI_STATE);
    };

    if (!canAnswerQuizzes) {
      return React.createElement(
        UnitPageShell,
        { title: 'Zugriff verweigert' },
        React.createElement(
          Container,
          { maxWidth: 'md', sx: { py: 6 } },
          React.createElement(Alert, { severity: 'error' }, 'Keine Berechtigung fuer Quizze.')
        )
      );
    }

    if (loading) {
      return React.createElement(
        Box,
        { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' } },
        React.createElement(CircularProgress, null)
      );
    }

    if (error) {
      return React.createElement(
        Box,
        { sx: { p: 3 } },
        React.createElement(Typography as any, { color: 'error' }, error)
      );
    }

    if (!currentQuestion) {
      return React.createElement(
        Box,
        { sx: { p: 3 } },
        React.createElement(
          Typography as any,
          null,
          'Keine Quizfragen gefunden. Bitte überprüfe die Datei public/assets/Quiz.xlsx.'
        )
      );
    }

    return React.createElement(
      Container,
      { 
        maxWidth: 'lg',
        sx: { 
          py: { xs: 3, md: 4 },
          minHeight: 'calc(100vh - 64px)'
        } 
      },
      React.createElement(
        Box,
        {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }
        },
        React.createElement(
          Box,
          { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 } },
          React.createElement(
            Typography as any,
            { 
              variant: 'h4',
              sx: { 
                fontWeight: 700,
                color: accentColor,
                mb: 2
              }
            },
            title
          ),
          canManageQuiz
            ? React.createElement(
                RouterLink,
                {
                  to: `/admin/quiz-editor?quiz=${encodeURIComponent(storageKey.toUpperCase())}`,
                  style: { textDecoration: 'none' }
                },
                React.createElement(Button, { variant: 'outlined' }, 'Quiz bearbeiten')
              )
            : null
        ),
        React.createElement(
          Box,
          {
            sx: {
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2,
              p: 2,
              bgcolor: darkMode ? hoverBgDark : hoverBgLight,
              borderRadius: 2
            }
          },
          React.createElement(Typography as any, { sx: { fontWeight: 600, fontSize: '0.95rem' } }, questionCounterLabel),
          React.createElement(Typography as any, { sx: { fontWeight: 600, fontSize: '0.95rem', color: accentColor } }, highScoreLabel),
          React.createElement(Typography as any, { sx: { fontWeight: 600, fontSize: '0.95rem' } }, successRateLabel),
          React.createElement(
            Box,
            { sx: { display: 'flex', alignItems: 'center', gap: 0.5 } },
            React.createElement(
              Tooltip as any,
              { title: jokerCount > 0 ? `Joker verf\u00fcgbar (${jokerCount})` : 'Kein Joker verf\u00fcgbar' },
              React.createElement(
                'span',
                { style: { display: 'inline-flex', alignItems: 'center' } },
                React.createElement('img', {
                  src: '/assets/joker.svg',
                  alt: 'Joker',
                  width: 28,
                  height: 28,
                  style: {
                    opacity: jokerCount > 0 ? 1 : 0.45,
                    filter: jokerCount > 0 ? 'none' : 'grayscale(100%)'
                  }
                })
              )
            ),
            React.createElement(Typography as any, { sx: { fontWeight: 600, fontSize: '0.9rem' } }, String(jokerCount)),
            React.createElement(
              Tooltip as any,
              { title: 'Info zum Joker' },
              React.createElement(
                'span',
                { style: { display: 'inline-flex' } },
                React.createElement(
                  IconButton,
                  {
                    size: 'small',
                    onClick: () => setShowJokerInfoDialog(true),
                    'aria-label': 'Joker Info anzeigen'
                  },
                  React.createElement(InfoOutlinedIcon, { fontSize: 'small' })
                )
              )
            )
          )
        ),
        React.createElement(
          Paper,
          {
            elevation: 2,
            sx: {
              p: { xs: 3, md: 4 },
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              bgcolor: theme.palette.background.paper
            }
          },
          React.createElement(
            Typography as any,
            { variant: 'h5', sx: { color: accentColor, fontWeight: 600, mb: 1 } },
            cardTitle
          ),
          React.createElement(
            Typography as any,
            { variant: 'h6', sx: { fontWeight: 600 } },
            currentQuestion.question
          ),
          React.createElement(
            FormGroup,
            { sx: { display: 'flex', flexDirection: 'column', gap: 1 } },
            currentQuestion.options.map((option, optionIndex) => {
              const correctIndexes = normalizeCorrectIndexes(
                currentQuestion.correctIndexes || [],
                currentQuestion.options.length
              );
              const isCorrectOption = correctIndexes.includes(optionIndex);
              const isSelected = selectedOptions.includes(optionIndex);
              const showFeedback = mode === 'feedback';
              const highlightColor = showFeedback
                ? isCorrectOption
                  ? darkMode
                    ? successBgDark
                    : successBgLight
                  : isSelected
                    ? darkMode
                      ? errorBgDark
                      : errorBgLight
                    : 'transparent'
                : 'transparent';

              return React.createElement(FormControlLabel, {
                key: `${optionIndex}-${option}`,
                control: React.createElement(Checkbox, {
                  color: isCorrectOption ? 'success' : 'primary',
                  checked: isSelected,
                  onChange: () => toggleOption(optionIndex),
                  disabled: showFeedback
                }),
                label: option,
                sx: {
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  backgroundColor: highlightColor,
                  '&:hover': {
                    bgcolor: darkMode ? hoverBgDark : hoverBgLight
                  }
                }
              });
            })
          ),
          feedback
            ? React.createElement(
                Typography as any,
                { variant: 'body1', sx: { color: feedbackColor, fontWeight: 600 } },
                feedback
              )
            : null,
          React.createElement(
            Box,
            {
              sx: {
                display: 'flex',
                gap: 2,
                justifyContent: 'space-between',
                flexWrap: 'wrap'
              }
            },
            React.createElement(
              Button,
              {
                variant: 'outlined',
                onClick: handleReset,
                sx: {
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': { 
                    borderColor: theme.palette.text.primary,
                    bgcolor: darkMode ? hoverBgDark : hoverBgLight
                  }
                }
              },
              'Reset'
            ),
            React.createElement(
              Box,
              { sx: { display: 'flex', alignItems: 'stretch', gap: 1.25, ml: 'auto' } },
              React.createElement(
                Tooltip as any,
                {
                  title: canUseJoker
                    ? `Joker einsetzen (${jokerCount} verf\u00fcgbar)`
                    : jokerCount > 0
                      ? 'Joker ist nur nach falscher Antwort nutzbar'
                      : 'Kein Joker verf\u00fcgbar'
                },
                React.createElement(
                  'span',
                  { style: { display: 'inline-flex' } },
                  React.createElement(
                    Button,
                    {
                      variant: canUseJoker ? 'contained' : 'outlined',
                      onClick: canUseJoker ? handleUseJoker : () => setShowJokerInfoDialog(true),
                      sx: {
                        width: 86,
                        minWidth: 86,
                        height: 64,
                        p: 0.75,
                        borderColor: accentColor,
                        color: canUseJoker ? '#fff' : accentColor,
                        bgcolor: canUseJoker ? accentColor : 'transparent',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 0.3,
                        '&:hover': {
                          borderColor: darkenColor(accentColor),
                          bgcolor: canUseJoker ? darkenColor(accentColor) : (darkMode ? hoverBgDark : hoverBgLight)
                        }
                      }
                    },
                    React.createElement('img', {
                      src: '/assets/joker.svg',
                      alt: 'Joker',
                      width: 34,
                      height: 34,
                      style: {
                        opacity: jokerCount > 0 ? 1 : 0.45,
                        filter: jokerCount > 0 ? 'none' : 'grayscale(100%)'
                      }
                    }),
                    React.createElement(Typography as any, { sx: { fontWeight: 700, fontSize: '0.78rem', lineHeight: 1 } }, String(jokerCount))
                  )
                )
              ),
              React.createElement(
                Button,
                {
                  variant: 'contained',
                  onClick: handlePrimaryAction,
                  disabled: submitDisabled,
                  sx: {
                    bgcolor: accentColor,
                    color: '#fff',
                    '&:hover': { bgcolor: darkenColor(accentColor) },
                    '&:disabled': { bgcolor: theme.palette.action.disabledBackground }
                  }
                },
                mode === 'feedback' && currentIndex + 1 >= totalQuestions ? 'Ergebnis' : 'Weiter'
              )
            )
          )
        )
      ),
      React.createElement(
        Dialog,
        { open: showResultDialog, onClose: handleDialogClose, fullWidth: true, maxWidth: 'sm' },
        React.createElement(DialogTitle, null, 'Quiz Ergebnis'),
        React.createElement(
          DialogContent,
          { dividers: true },
          resultSummary
            ? React.createElement(
                Box,
                { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
                React.createElement(
                  Typography as any,
                  { variant: 'body1' },
                  `Dein Ergebnis: ${resultSummary.score} von ${resultSummary.totalQuestions} Fragen richtig (${resultSummary.percentage}%).`
                ),
                React.createElement(
                  Typography as any,
                  { variant: 'body1' },
                  `Highscore: ${resultSummary.bestScore}%`
                ),
                resultSummary.earnedJokerCount > 0
                  ? React.createElement(
                      Typography as any,
                      { variant: 'body1', sx: { color: accentColor, fontWeight: 600 } },
                      `Du hast ${resultSummary.earnedJokerCount} Joker erhalten.`
                    )
                  : null,
                resultSummary.newHighscore
                  ? React.createElement(
                      Typography as any,
                      { variant: 'body1', sx: { color: successColor, fontWeight: 600 } },
                      'Herzlichen Glückwunsch zum neuen Highscore!'
                    )
                  : null,
                React.createElement(
                  Typography as any,
                  { variant: 'body1' },
                  `Abgeschlossene Durchgänge: ${resultSummary.totalCompleted}`
                ),
                React.createElement(
                  Typography as any,
                  { variant: 'body1' },
                  `Gesamte Erfolgsquote: ${resultSummary.successRate.toFixed(2)}%`
                )
              )
            : null
        ),
        React.createElement(
          DialogActions,
          null,
          React.createElement(Button, { onClick: handleDialogClose, variant: 'contained' }, 'OK')
        )
      ),
      React.createElement(
        Dialog,
        { open: showJokerInfoDialog, onClose: () => setShowJokerInfoDialog(false), fullWidth: true, maxWidth: 'sm' },
        React.createElement(DialogTitle, null, 'Joker Funktion'),
        React.createElement(
          DialogContent,
          { dividers: true },
          React.createElement(
            DialogContentText,
            { sx: { color: textPrimary } },
            'Der Joker gilt f\u00fcr alle Quizseiten zusammen und wird global gez\u00e4hlt. ',
            'Du verdienst nach jedem abgeschlossenen Quiz einen weiteren Joker. ',
            'Wenn du bei einer Frage falsch liegst, kannst du den Joker einsetzen und genau diese letzte Frage erneut beantworten. ',
            'Beim Einsetzen wird der Joker sofort verbraucht.'
          )
        ),
        React.createElement(
          DialogActions,
          null,
          React.createElement(Button, { onClick: () => setShowJokerInfoDialog(false), variant: 'contained' }, 'Verstanden')
        )
      )
    );
  };

  Component.displayName = `QuizPage_${storageKey}`;

  return Component;
};

const loadProgress = (key: string): ProgressState => {
  if (typeof window === 'undefined') return DEFAULT_PROGRESS;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return DEFAULT_PROGRESS;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.index === 'number' && typeof parsed?.score === 'number') {
      const parsedOutcomes =
        parsed?.questionOutcomes && typeof parsed.questionOutcomes === 'object'
          ? Object.fromEntries(
              Object.entries(parsed.questionOutcomes).filter(([k, v]) => {
                const index = Number(k);
                return Number.isInteger(index) && index >= 0 && (v === 'correct' || v === 'wrong');
              })
            )
          : {};
      return {
        index: Math.max(0, parsed.index),
        score: Math.max(0, parsed.score),
        questionOutcomes: parsedOutcomes as Record<number, 'correct' | 'wrong'>
      };
    }
    return DEFAULT_PROGRESS;
  } catch (err) {
    console.warn('Fortschritt konnte nicht gelesen werden:', err);
    return DEFAULT_PROGRESS;
  }
};

const sanitizeOutcomeMap = (
  value: unknown,
  maxQuestionCount?: number
): Record<number, 'correct' | 'wrong'> => {
  if (!value || typeof value !== 'object') return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(([key, state]) => {
      const index = Number(key);
      if (!Number.isInteger(index) || index < 0) return false;
      if (typeof maxQuestionCount === 'number' && index >= maxQuestionCount) return false;
      return state === 'correct' || state === 'wrong';
    })
  ) as Record<number, 'correct' | 'wrong'>;
};

const loadOutcomeLocks = (key: string): Record<number, 'correct' | 'wrong'> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return sanitizeOutcomeMap(parsed);
  } catch {
    return {};
  }
};

const saveOutcomeLocks = (key: string, outcomes: Record<number, 'correct' | 'wrong'>): void => {
  if (typeof window === 'undefined') return;
  try {
    const normalized = sanitizeOutcomeMap(outcomes);
    window.localStorage.setItem(key, JSON.stringify(normalized));
  } catch {
    // ignore lock persistence errors
  }
};

const clearOutcomeLocks = (key: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore lock cleanup errors
  }
};

const loadStats = (key: string): StatsState => {
  if (typeof window === 'undefined') return DEFAULT_STATS;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return DEFAULT_STATS;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.completed === 'number' &&
      typeof parsed?.totalCorrect === 'number' &&
      typeof parsed?.bestScore === 'number'
    ) {
      return {
        completed: Math.max(0, parsed.completed),
        totalCorrect: Math.max(0, parsed.totalCorrect),
        bestScore: Math.max(0, parsed.bestScore)
      };
    }
    return DEFAULT_STATS;
  } catch (err) {
    console.warn('Statistiken konnten nicht gelesen werden:', err);
    return DEFAULT_STATS;
  }
};

const loadUiState = (key: string): UiState => {
  if (typeof window === 'undefined') return DEFAULT_UI_STATE;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return DEFAULT_UI_STATE;
    const parsed = JSON.parse(raw);
    const parsedMode = parsed?.mode === 'feedback' ? 'feedback' : 'answering';
    const parsedSelected = Array.isArray(parsed?.selectedOptions)
      ? parsed.selectedOptions.filter((idx: unknown) => typeof idx === 'number' && Number.isInteger(idx) && idx >= 0)
      : [];
    const parsedFeedback = typeof parsed?.feedback === 'string' ? parsed.feedback : null;
    const parsedWasCorrect = typeof parsed?.wasCorrect === 'boolean' ? parsed.wasCorrect : null;
    const parsedAnsweredIndex =
      typeof parsed?.answeredIndex === 'number' && Number.isInteger(parsed.answeredIndex) && parsed.answeredIndex >= 0
        ? parsed.answeredIndex
        : null;

    return {
      mode: parsedMode,
      selectedOptions: parsedSelected,
      feedback: parsedFeedback,
      wasCorrect: parsedWasCorrect,
      answeredIndex: parsedAnsweredIndex
    };
  } catch (err) {
    console.warn('Quiz-UI-Zustand konnte nicht gelesen werden:', err);
    return DEFAULT_UI_STATE;
  }
};

const darkenColor = (hex: string): string => {
  const value = hex.startsWith('#') ? hex.slice(1) : hex;
  if (value.length !== 6) return tokens.colors.primaryHover;
  const num = parseInt(value, 16);
  const r = Math.max(0, ((num >> 16) & 0xff) - 20);
  const g = Math.max(0, ((num >> 8) & 0xff) - 20);
  const b = Math.max(0, (num & 0xff) - 20);
  const combined = (r << 16) | (g << 8) | b;
  return `#${combined.toString(16).padStart(6, '0')}`;
};

const loadGlobalJokerCount = (): number => {
  if (typeof window === 'undefined') return 1;
  try {
    const countRaw = window.localStorage.getItem(GLOBAL_JOKER_COUNT_STORAGE_KEY);
    if (countRaw != null) {
      const parsed = Number(countRaw);
      return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 1;
    }

    const legacyRaw = window.localStorage.getItem(LEGACY_GLOBAL_JOKER_STORAGE_KEY);
    if (legacyRaw === '1' || legacyRaw === 'true') {
      return 1;
    }

    return 1;
  } catch (err) {
    console.warn('Joker-Zaehler konnte nicht gelesen werden:', err);
    return 1;
  }
};

const saveGlobalJokerCount = (count: number): void => {
  if (typeof window === 'undefined') return;
  try {
    const normalized = Math.max(0, Math.floor(Number.isFinite(count) ? count : 0));
    window.localStorage.setItem(GLOBAL_JOKER_COUNT_STORAGE_KEY, String(normalized));
  } catch (err) {
    console.warn('Joker-Zaehler konnte nicht gespeichert werden:', err);
  }
};
